import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { Eye, EyeOff, ArrowLeft, Plus, Minus, Users, Repeat } from 'lucide-react';
import '../../styles/auth.css';
import logo from '../../assets/logo.png';

const calculateAge = (birthDate) => {
  const today = new Date();
  let birth;
  if (birthDate.includes('/')) {
    const parts = birthDate.split('/');
    if (parts.length === 3 && parts[2].length === 4) {
      birth = new Date(parts[2], parts[0] - 1, parts[1]);
    } else return 0;
  } else {
    birth = new Date(birthDate);
  }
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const SignUp = () => {
  const navigate = useNavigate();
  const { signUpWithEmail } = useAuth();

  // Wizard step: 0=credentials, 1=birthday, 2=trade-or-kids, 3=parent-sub-flow
  const [step, setStep] = useState(0);

  // Step 0: Credentials
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Step 1: Birthday
  const [birthDate, setBirthDate] = useState('');

  // Step 2: Choice (adults only)
  // No state needed - just button clicks

  // Step 3: Parent sub-flow
  const [parentWillTrade, setParentWillTrade] = useState(false);
  const [kids, setKids] = useState([{ name: '' }]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDateInput = (value) => {
    const digits = value.replace(/\D/g, '');
    let formatted = '';
    if (digits.length > 0) formatted = digits.slice(0, 2);
    if (digits.length > 2) formatted += '/' + digits.slice(2, 4);
    if (digits.length > 4) formatted += '/' + digits.slice(4, 8);
    setBirthDate(formatted);
  };

  const addKid = () => {
    if (kids.length < 10) {
      setKids([...kids, { name: '' }]);
    }
  };

  const removeKid = (index) => {
    if (kids.length > 1) {
      setKids(kids.filter((_, i) => i !== index));
    }
  };

  const updateKidName = (index, name) => {
    const updated = [...kids];
    updated[index] = { name };
    setKids(updated);
  };

  const fireSignup = async (signupType, extraMeta = {}) => {
    setLoading(true);
    setError('');

    const parts = birthDate.split('/');
    const dbDate = parts.length === 3
      ? `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`
      : null;

    const metadata = {
      birth_date: dbDate,
      signup_type: signupType,
      ...extraMeta,
    };

    const { data, error: signupError } = await signUpWithEmail(email, password, metadata);

    if (signupError) {
      setError(signupError.message);
      setLoading(false);
      return;
    }

    // Upsert basic user row so downstream checks work immediately
    if (data?.user) {
      await supabase.from('users').upsert({
        auth_id: data.user.id,
        email: email.toLowerCase(),
        signup_type: signupType,
        birth_date: dbDate,
        is_under_18: calculateAge(birthDate) < 18,
        is_parent: signupType === 'parent',
        parent_will_trade: extraMeta.parent_will_trade || false,
      }, { onConflict: 'auth_id' });
    }

    navigate('/');
  };

  // Step 0: Validate credentials and advance
  const handleCredentialsNext = (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setStep(1);
  };

  // Step 1: Validate birthday and route
  const handleBirthdayNext = () => {
    setError('');
    if (!birthDate || birthDate.length !== 10) {
      setError('Please enter a valid date (MM/DD/YYYY)');
      return;
    }

    // Validate month/day/year ranges
    const parts = birthDate.split('/');
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > new Date().getFullYear()) {
      setError('Please enter a valid date');
      return;
    }

    // Validate actual calendar date (catches things like Feb 30)
    const testDate = new Date(year, month - 1, day);
    if (testDate.getMonth() !== month - 1 || testDate.getDate() !== day) {
      setError('Please enter a valid date');
      return;
    }

    const age = calculateAge(birthDate);
    if (age < 5 || age > 120) {
      setError('Please enter a valid birth date');
      return;
    }

    if (age < 18) {
      // Under 18 - fire signup immediately as 'user'
      fireSignup('user');
    } else {
      // 18+ - advance to trade/kids choice
      setStep(2);
    }
  };

  // Step 2: "Here to trade" fires signup, "Setting up for kids" advances
  const handleTradeChoice = () => {
    fireSignup('user');
  };

  const handleKidsChoice = () => {
    setStep(3);
  };

  // Step 3: Parent sub-flow submit
  const handleParentSubmit = () => {
    const filteredKids = kids.filter(k => k.name.trim());
    fireSignup('parent', {
      parent_will_trade: parentWillTrade,
      kids: filteredKids,
    });
  };

  const handleBack = () => {
    setError('');
    if (step > 0) {
      setStep(step - 1);
    }
  };

  // Step indicator
  const totalSteps = 3; // We show 3 dots max (step 3 replaces step 2 for parent path)
  const activeStep = Math.min(step, 2);

  return (
    <div className="auth-container">
      <div className="auth-content">
        {/* Header with back button */}
        <div className="auth-header">
          {step > 0 && (
            <button className="wizard-back-btn" onClick={handleBack} disabled={loading}>
              <ArrowLeft size={20} />
            </button>
          )}
          <img
            src={logo}
            alt="CardChase"
            style={{
              width: 100,
              height: 100,
              borderRadius: 20,
              marginBottom: 12,
              objectFit: 'cover'
            }}
          />
          <h1 className="auth-title">
            {step === 0 && 'Create account'}
            {step === 1 && 'Your birthday'}
            {step === 2 && 'How will you use CardChase?'}
            {step === 3 && 'Family setup'}
          </h1>
          <p className="auth-subtitle">
            {step === 0 && 'Get started with CardChase'}
            {step === 1 && 'This helps us set up your account'}
            {step === 2 && 'Choose your path'}
            {step === 3 && 'Set up your family account'}
          </p>
        </div>

        {/* Step dots */}
        <div className="wizard-dots">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className={`wizard-dot ${activeStep === i ? 'active' : ''} ${activeStep > i ? 'done' : ''}`}
            />
          ))}
        </div>

        {error && <div className="auth-error">{error}</div>}

        {/* Step 0: Credentials */}
        {step === 0 && (
          <form className="auth-form" onSubmit={handleCredentialsNext}>
            <div className="input-group">
              <label className="input-label">Email</label>
              <input
                type="email"
                className="auth-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="input-group">
              <label className="input-label">Password</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="auth-input"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Confirm Password</label>
              <div className="password-wrapper">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  className="auth-input"
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirm(!showConfirm)}
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? <div className="spinner" /> : 'Next'}
            </button>
          </form>
        )}

        {/* Step 1: Birthday */}
        {step === 1 && (
          <div className="auth-form">
            <div className="input-group">
              <label className="input-label">Date of Birth</label>
              <input
                type="text"
                inputMode="numeric"
                className="auth-input"
                placeholder="MM/DD/YYYY"
                value={birthDate}
                onChange={(e) => handleDateInput(e.target.value)}
                maxLength={10}
                disabled={loading}
              />
            </div>

            <button
              type="button"
              className="auth-submit"
              onClick={handleBirthdayNext}
              disabled={loading}
            >
              {loading ? <div className="spinner" /> : 'Next'}
            </button>
          </div>
        )}

        {/* Step 2: Trade or Kids choice cards */}
        {step === 2 && (
          <div className="auth-form">
            <div className="wizard-choice-cards">
              <button
                className="wizard-choice-card"
                onClick={handleTradeChoice}
                disabled={loading}
              >
                <Repeat size={32} />
                <span className="wizard-choice-title">Here to trade</span>
                <span className="wizard-choice-desc">
                  Connect with traders, build your collection, and meet up to trade cards
                </span>
              </button>

              <button
                className="wizard-choice-card"
                onClick={handleKidsChoice}
                disabled={loading}
              >
                <Users size={32} />
                <span className="wizard-choice-title">Setting up for kids</span>
                <span className="wizard-choice-desc">
                  Create a family account and manage your children's trading profiles
                </span>
              </button>
            </div>

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                <div className="spinner" />
              </div>
            )}
          </div>
        )}

        {/* Step 3: Parent sub-flow */}
        {step === 3 && (
          <div className="auth-form">
            {/* Will you also trade? */}
            <div className="input-group">
              <label className="input-label">Will you also trade cards?</label>
              <div className="wizard-toggle-row">
                <button
                  type="button"
                  className={`wizard-toggle-btn ${parentWillTrade ? 'active' : ''}`}
                  onClick={() => setParentWillTrade(true)}
                  disabled={loading}
                >
                  Yes, I'll trade too
                </button>
                <button
                  type="button"
                  className={`wizard-toggle-btn ${!parentWillTrade ? 'active' : ''}`}
                  onClick={() => setParentWillTrade(false)}
                  disabled={loading}
                >
                  No, just managing
                </button>
              </div>
            </div>

            {/* Add your kids */}
            <div className="input-group">
              <label className="input-label">Add your kids</label>
              <div className="wizard-kids-list">
                {kids.map((kid, i) => (
                  <div key={i} className="wizard-kid-row">
                    <input
                      type="text"
                      className="auth-input"
                      placeholder={`Child ${i + 1} first name`}
                      value={kid.name}
                      onChange={(e) => updateKidName(i, e.target.value)}
                      disabled={loading}
                    />
                    {kids.length > 1 && (
                      <button
                        type="button"
                        className="wizard-kid-remove"
                        onClick={() => removeKid(i)}
                        disabled={loading}
                      >
                        <Minus size={16} />
                      </button>
                    )}
                  </div>
                ))}
                {kids.length < 10 && (
                  <button
                    type="button"
                    className="wizard-kid-add"
                    onClick={addKid}
                    disabled={loading}
                  >
                    <Plus size={16} />
                    <span>Add another child</span>
                  </button>
                )}
              </div>
            </div>

            <button
              type="button"
              className="auth-submit"
              onClick={handleParentSubmit}
              disabled={loading}
            >
              {loading ? <div className="spinner" /> : 'Create Account'}
            </button>
          </div>
        )}

        <div className="auth-footer">
          <span className="auth-footer-text">
            Have an account?{' '}
            <Link to="/login" className="auth-footer-link">Sign in</Link>
          </span>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
